import './RAStrategy.scss';

import React from 'react';
import PropTypes from 'prop-types';
import RAContributionBar from '../RAContributionBar/RAContributionBar';
import { formatText } from 'core/i18n';
import { isMobile } from 'utilities/browser';
import CurrencyHelper from 'utilities/RACurrencyHelper';
import { EURO_SYMBOL } from 'constants/RAConstants';
import { debounce } from 'utilities/helperFunctions';

const BIG_BAR_HEIGHT = 30;
const SMALL_BAR_HEIGHT = 15;
const MIN_SMALL_BAR_WIDTH = 'SMALL'; // 2.5% of goal
const MIN_BIG_BAR_WIDTH = 'BIG'; // 5% of goal
const MIN_SMALL_BAR_WIDTH_FACTOR = 0.025; // 2.5%
const MIN_BIG_BAR_WIDTH_FACTOR = 0.05; // 5%
const MAX_SMALL_BAR_WIDTH = 60;
const BLUE = '#002b45';
const GRAY = '#8396A4';
const RED = '#ED0A00';
const GREEN = '#15A215';
const LIGHTGREEN = '#BAD866';
const END_BAR_HEIGHT = 0;
const MIN_TOP_POSITION = -15;
const MAX_TOP_POSITION = -30;
const END_BAR_BOTTOM = 5;
const END_BAR_TOP = 12;
const PLUS_SIGN = '+';
const MINUS_SIGN = '-';
const END_LINE_STYLE = {
    transform: 'translate(0px, calc(-30%))',
};
const MOBILE_BAR_PLUS = isMobile() ? 0 : 0;

class RAStrategy extends React.Component {
    constructor(props) {
        super(props);

        this.barContainerRef = null;
        this.isBarContainerRendered = false;
        this.updateBarWidths = debounce(this.updateBarWidths.bind(this), 300);
    }

    componentDidMount() {
        window.addEventListener('resize', this.updateBarWidths);
        if (this.isBarContainerRendered && this.barContainerRef) {
            const contributionWidth = Math.max(
                this.getInitialContributionAmountBarWidth(),
                this.getMinWidthOfBar(MIN_BIG_BAR_WIDTH)
            );
            // This function is only called when a barContainerRef got undefined in render() method
            // And calculations of width of bars are dependent on this.barContainerRef.
            if (contributionWidth) {
                this.isBarContainerRendered = false;
                this.updateBarWidths();
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateBarWidths);
    }

    setBarContainerRef = ref => {
        if (ref) {
            this.barContainerRef = ref;
        }
    };

    // This function is only called when a resize event is triggered,
    // we need to force an update since props don't change and bars' width
    // needs to be recalculated.
    updateBarWidths() {
        this.forceUpdate();
    }

    buildPositiveBar(numerator, plotBarWidth, goalWidth, medProfitability) {
        const amountText =
            PLUS_SIGN +
            CurrencyHelper.getFormattedString({
                amount: numerator,
                currency: EURO_SYMBOL,
            });
        const max = MAX_SMALL_BAR_WIDTH + goalWidth - plotBarWidth;
        let width = this.getBarWidth(numerator, true);
        let endingBar = null;
        let delimiter = null;
        let showDelimiter = false;
        const offset = 100;
        if (parseInt(width, 10) > parseInt(max, 10) + offset) {
            width = Math.min(width, max + offset);
            showDelimiter = true;
        }

        const endingColor = medProfitability ? LIGHTGREEN : GREEN;
        const minWidth = this.getMinWidthOfBar(MIN_SMALL_BAR_WIDTH);
        width = Math.max(width, minWidth); // Set minimum width

        if (endingColor === LIGHTGREEN) {
            endingBar = <RAContributionBar.End position="end" color={endingColor} text={amountText} />;
        } else {
            endingBar = (
                <RAContributionBar.End
                    position="end"
                    color={endingColor}
                    text={amountText}
                    height={END_BAR_HEIGHT}
                    bottomCircular={END_BAR_BOTTOM}
                    topCircular={END_BAR_TOP}
                />
            );
        }

        if (parseInt(plotBarWidth, 10) + parseInt(width, 10) > parseInt(goalWidth, 10) + MAX_SMALL_BAR_WIDTH) {
            const newWidth = goalWidth + MAX_SMALL_BAR_WIDTH - plotBarWidth;
            width = parseInt(newWidth, 10); // Set maximum width
            showDelimiter = true;
        }

        if (showDelimiter) {
            delimiter = <RAContributionBar.ArrowsChildren size={SMALL_BAR_HEIGHT} reverse />;
        }

        if (medProfitability) {
            return (
                <RAContributionBar
                    width={width + MOBILE_BAR_PLUS}
                    color={LIGHTGREEN}
                    height={SMALL_BAR_HEIGHT}
                    endingBar={endingBar}
                    top={MIN_TOP_POSITION}
                    left={width + MOBILE_BAR_PLUS}
                >
                    {delimiter}
                </RAContributionBar>
            );
        }

        return (
            <RAContributionBar
                width={width + MOBILE_BAR_PLUS}
                color={GREEN}
                height={SMALL_BAR_HEIGHT}
                endingBar={endingBar}
                top={MAX_TOP_POSITION}
            >
                {delimiter}
            </RAContributionBar>
        );
    }

    buildNegativeBar(numerator, max) {
        const amountText =
            MINUS_SIGN +
            CurrencyHelper.getFormattedString({
                amount: numerator,
                currency: EURO_SYMBOL,
            });
        const startingBar = <RAContributionBar.End position="start" color={RED} text={amountText} />;
        let width = this.getBarWidth(numerator, true);
        const delimiter = null;
        if (parseInt(width, 10) > parseInt(max, 10)) {
            width = Math.min(width, max + 20);
        }
        const minWidth = this.getMinWidthOfBar(MIN_SMALL_BAR_WIDTH);
        width = Math.max(width, minWidth);

        return (
            <RAContributionBar
                width={width + MOBILE_BAR_PLUS}
                color={RED}
                height={SMALL_BAR_HEIGHT}
                startingBar={startingBar}
                top={MIN_TOP_POSITION}
            >
                {delimiter}
            </RAContributionBar>
        );
    }

    getMinWidthOfBar(size) {
        if (!this.barContainerRef) return 0;
        const minFactor = size === MIN_SMALL_BAR_WIDTH ? MIN_SMALL_BAR_WIDTH_FACTOR : MIN_BIG_BAR_WIDTH_FACTOR;

        return this.barContainerRef.clientWidth * minFactor;
    }

    getBarWidth(numerator, scalable) {
        const { goal, graphScaledToMaxValue, graphScaledToMaxEnabled } = this.props;
        if (!this.barContainerRef || goal == null || goal === 0) return 0;

        const containerWidth = this.barContainerRef.clientWidth;
        // apply scalable factor to scale bar view if bar is less than 30% of goal
        const scalableFactor = graphScaledToMaxEnabled && scalable ? graphScaledToMaxValue : 1;

        return ((containerWidth * numerator) / goal) * scalableFactor;
    }

    // Left bar never should be greater than the goal
    getInitialContributionAmountBarWidth() {
        const { initialContributionAmount, goal } = this.props;
        const minWidth = this.getMinWidthOfBar(MIN_BIG_BAR_WIDTH);
        const initialContributionAmountWidth = this.getBarWidth(Math.min(initialContributionAmount, goal), true);

        return Math.max(initialContributionAmountWidth, minWidth);
    }

    getRecurringContributionAmountBarWidth() {
        const { recurringContributionAmount } = this.props;

        return this.getBarWidth(recurringContributionAmount, true);
    }

    buildProfitabilityBars = () => {
        // hide ProfitabilityBars if intial and/or recurring value higher than objective value.
        if (!this.barContainerRef || this.props.isLeftBarExceedGoal) {
            return null;
        }
        let negativeBar;
        const { goal, minProfitability, medProfitability, maxProfitability } = this.props;
        const leftBarWidth = this.getInitialContributionAmountBarWidth();
        const recurringBarWidth = this.getRecurringContributionAmountBarWidth();
        const rightBarWidth = Math.min(this.getRecurringContributionAmountBarWidth(), this.getRightBarMaxWidth());
        if (medProfitability) {
            negativeBar = this.buildPositiveBar(
                medProfitability,
                leftBarWidth + rightBarWidth,
                this.getBarWidth(goal),
                true
            );
        } else {
            negativeBar = this.buildNegativeBar(minProfitability, rightBarWidth);
        }
        const positiveBar = this.buildPositiveBar(
            maxProfitability,
            leftBarWidth + rightBarWidth,
            this.getBarWidth(goal)
        );

        const topPosition =
            recurringBarWidth >= 0 &&
            Math.round(recurringBarWidth) <= Math.round(rightBarWidth) &&
            leftBarWidth <= this.getBarWidth(goal)
                ? '100%'
                : '25%';

        return (
            <RAContributionBar
                width={0}
                color=""
                height={BIG_BAR_HEIGHT}
                endingBar={negativeBar}
                startingBar={positiveBar}
                startingBarTopPosition={topPosition}
                endingBarTopPosition={topPosition}
            >
                <div className="ra-strategy__bar-end-line" style={END_LINE_STYLE} />
            </RAContributionBar>
        );
    };

    getRightBarMaxWidth() {
        const { goal } = this.props;
        const leftBarWidth = this.getInitialContributionAmountBarWidth();
        const offsetAfterGoal = 0;

        return offsetAfterGoal + this.getBarWidth(goal) - leftBarWidth;
    }

    delimiterForleftBar() {
        const { goal, initialContributionAmount } = this.props;
        const leftBarWidth = this.getInitialContributionAmountBarWidth();
        const rightBarWidth = this.getRecurringContributionAmountBarWidth();
        const delimiterEnabled =
            initialContributionAmount == goal && leftBarWidth + rightBarWidth > this.getBarWidth(goal);
        if (initialContributionAmount > goal || delimiterEnabled) {
            return <RAContributionBar.ArrowsChildren size={BIG_BAR_HEIGHT} reverse />;
        }
    }

    buildRightBar = () => {
        const { goal } = this.props;
        const rightBarWidth = this.getRecurringContributionAmountBarWidth();
        const maxRightBarWidth = this.getRightBarMaxWidth();

        let delimiter = null;
        let width = rightBarWidth;
        const leftBarWidth = this.getInitialContributionAmountBarWidth();

        if (leftBarWidth >= this.getBarWidth(goal)) {
            width = END_BAR_HEIGHT;
        }
        const minWidth = this.getMinWidthOfBar(MIN_BIG_BAR_WIDTH);
        if (width > 0) {
            width = Math.max(width, minWidth); // set min width
        }
        if (rightBarWidth > maxRightBarWidth) {
            if (leftBarWidth + rightBarWidth > this.getBarWidth(goal) && leftBarWidth < this.getBarWidth(goal)) {
                delimiter = <RAContributionBar.ArrowsChildren size={BIG_BAR_HEIGHT} reverse />;
                width = maxRightBarWidth;
            }
        }

        return (
            <RAContributionBar
                width={width + MOBILE_BAR_PLUS}
                color={GRAY}
                height={BIG_BAR_HEIGHT}
                renderAfter={this.buildProfitabilityBars}
            >
                {delimiter}
            </RAContributionBar>
        );
    };

    render() {
        const { goalBar } = this.props;
        const minWidth = this.getMinWidthOfBar(MIN_BIG_BAR_WIDTH);
        const leftBarWidth = Math.max(this.getInitialContributionAmountBarWidth(), minWidth);
        const delimiter = this.delimiterForleftBar();

        if (!leftBarWidth && !minWidth) {
            this.isBarContainerRendered = true;
        }

        return (
            <div className="ra-strategy__container" ref={this.setBarContainerRef}>
                {goalBar}
                <RAContributionBar
                    width={leftBarWidth + MOBILE_BAR_PLUS}
                    color={BLUE}
                    height={BIG_BAR_HEIGHT}
                    renderAfter={this.buildRightBar}
                >
                    {delimiter}
                </RAContributionBar>
            </div>
        );
    }
}

RAStrategy.Description = ({ id }) => (
    <div className="ra-strategy__header">
        <i className={`icons icons--icon-strategy-small icons--icon-strategy-small--${id} hidden-xs`} />
        <div className="ra-strategy__description">
            <div className="ra-strategy__name hidden-xs">{formatText(`raStrategyItem-raStrategyItem_${id}`)}</div>
            <div className="ra-strategy__risk">{formatText(`raStrategy-${id}Risk`)}</div>
        </div>
    </div>
);

RAStrategy.Description.propTypes = {
    id: PropTypes.number.isRequired,
};

RAStrategy.Description.displayName = 'RAStrategy.Description';

RAStrategy.propTypes = {
    initialContributionAmount: PropTypes.number.isRequired,
    minProfitability: PropTypes.number.isRequired,
    medProfitability: PropTypes.number.isRequired,
    maxProfitability: PropTypes.number.isRequired,
    recurringContributionAmount: PropTypes.number.isRequired,
    goal: PropTypes.number.isRequired,
    goalBar: PropTypes.element,
    graphScaledToMaxEnabled: PropTypes.bool.isRequired,
    graphScaledToMaxValue: PropTypes.number.isRequired,
    isLeftBarExceedGoal: PropTypes.bool.isRequired,
};

module.exports = RAStrategy;
